import {
  LayoutDashboard, LineChart, Users, Wifi, Tv, CreditCard, RefreshCw,
  MessageSquare, Server, BarChart2, Settings, UserCog, Antenna, Wallet,
  Router, UserCheck, GitBranch, Send, History, Trash2, FileClock, ClipboardList,
  Cpu, Link, Gauge, FileSpreadsheet, ShieldCheck, Phone, Image, Receipt, UserPlus, Search,
  StickyNote, ScrollText, Landmark, Ticket, KeyRound, ListChecks, Pencil, Repeat,
  MessageCircle, MessagesSquare, FlaskConical, GitCompareArrows, UserX, PlusSquare, Replace, CalendarClock, CalendarCheck, Package, Bot, PhoneCall, UserMinus, BadgeCheck, LayoutGrid,
} from 'lucide-react';

import { IS_LAXMI_HOST } from '../../utils/demo';
import { getPageTitle as techTitle } from '../technical/technicalNavConfig';

// Single source of truth for the admin nav — shared by the web sidebar
// (AdminLayout) and the app/tablet bottom-nav shell (AdminAppLayout).
export const navItems = [
  { section: 'DAILY' },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/insights', icon: LineChart, label: 'Insights', adminOnly: true },
  // Moved up from ADMIN SETTINGS into MAIN (user request) — it's a daily-use inbox, not a setting.
  // `badge` is rendered by AdminLayout only (see InboxUnreadBadge) — the bottom-nav shell has
  // no room for one.
  { to: '/whatsapp-inbox', icon: MessagesSquare, label: 'WhatsApp Inbox', moduleKey: 'whatsappInbox', badge: 'whatsappUnread' },
  { to: '/notes', icon: StickyNote, label: 'Reminders', adminOnly: true, moduleKey: 'notes' },
  { to: '/connection-requests', icon: UserPlus, label: 'Leads', moduleKey: 'connectionRequests' },
  { section: 'CUSTOMERS & BILLING' },
  {
    icon: Users, label: 'Customers', key: 'customers', moduleKey: 'customers', groupTo: '/customers',
    children: [
      { to: '/customers?customerType=catv', icon: Tv, label: 'CableTV' },
      { to: '/customers?customerType=broadband', icon: Wifi, label: 'Broadband' },
    ],
  },
  // Hidden from nav for now (2026-09-06 user request: not used right now) — route/page
  // (SearchPage, /search-customer) and moduleKey both left intact, so this is a quick re-add.
  // { to: '/search-customer', icon: Search, label: 'Search Customer', moduleKey: 'customers' },
  { to: '/renewals', icon: RefreshCw, label: 'Renewals', moduleKey: 'renewals' },
  {
    icon: CreditCard, label: 'Payments', key: 'payments', moduleKey: 'payments', groupTo: '/payments',
    children: [
      { to: '/payments', icon: CreditCard, label: 'All Payments' },
      { to: '/payments/online', icon: Landmark, label: 'Online Payments' },
      { to: '/payments/advance', icon: Receipt, label: 'Advance Receipts' },
      { to: '/payment-screenshots', icon: Image, label: 'Payment Screenshots', adminOnly: true },
    ],
  },
  {
    // The three "someone wants something from us" queues under one entry. Each child keeps
    // its own moduleKey so a Site Role granting only Service Requests still shows only that
    // one child (see AdminLayout's per-child gate) — unlike the Customer Updates group
    // below, which gates at the parent only.
    icon: MessageSquare, label: 'Complaints/Requests', key: 'complaintsRequests', groupTo: '/complaints',
    children: [
      { to: '/complaints', icon: MessageSquare, label: 'Complaints', moduleKey: 'complaints' },
      { to: '/service-requests', icon: ClipboardList, label: 'Service Requests', moduleKey: 'serviceRequests' },
      // Same underlying data as Service Requests, just filtered to disconnection + package_change
      // — reuses that moduleKey rather than a new permission entry.
      { to: '/admin-lookup', icon: Search, label: 'Admin Lookup', moduleKey: 'serviceRequests' },
    ],
  },
  { to: '/plans', icon: Wifi, label: 'Plans & Packages', adminOnly: true, moduleKey: 'plans' },
  { section: 'REPORTS' },
  { to: '/reports', icon: BarChart2, label: 'Reports', moduleKey: 'reports' },
  { to: '/collection-sheet', icon: Receipt, label: 'Collection Sheet', adminOnly: true, moduleKey: 'reports' },
  { to: '/reconnection-calls', icon: PhoneCall, label: 'Reconnection Calls', moduleKey: 'reconnectionLeads' },
  // Same data source and permission as Reconnection Calls, but NOC included and read-only.
  { to: '/discontinued-catv', icon: UserMinus, label: 'Discontinued CableTV', moduleKey: 'reconnectionLeads' },
  { section: 'NETWORK' },
  { to: '/stb-inventory', icon: Antenna, label: 'STB Inventory', moduleKey: 'customers' },
  { to: '/devices', icon: Server, label: 'Device Inventory', moduleKey: 'devices' },
  { to: '/technical/dashboard', icon: Gauge, label: 'Network Overview', moduleKey: 'technicalDashboard' },
  { to: '/olt', icon: Router, label: 'OLT Management', moduleKey: 'olt' },
  // OLT Logs now lives as a tab inside System Logs (/change-ledger?tab=olt); /olt-logs redirects there.
  // Hidden from nav for now (2026-09-07 user request) — route/page (OltInfoV2Page, /olt-info-v2)
  // left intact, so this is a quick re-add, not a removal.
  // { to: '/olt-info-v2', icon: Antenna, label: 'OLT Info V2.0', adminOnly: true },
  // Hidden from nav for now (2026-09-07 user request) — route/page (TestingPage, /testing)
  // left intact, so this is a quick re-add, not a removal.
  // { to: '/testing', icon: FlaskConical, label: 'Testing', adminOnly: true },
  { to: '/acs', icon: Cpu, label: 'ACS (TR-069)', adminOnly: true, moduleKey: 'acs' },
  { to: '/fiber-map', icon: GitBranch, label: 'Fiber Map', moduleKey: 'fiberMap' },
  // Hidden from nav for now (2026-09-02 user request) — route/page (IMBSPage, /imbs) and
  // moduleKey both left intact, so this is a quick re-add, not a removal.
  // { to: '/imbs', icon: Link2, label: 'Netzur Sync', adminOnly: true, moduleKey: 'imbs' },
  { section: 'GTPL' },
  {
    // Standard adminOnly + moduleKey gate, same as every other section — any admin sees
    // it, and a Site Role granting the gtplSaathi 'view' right (moduleRegistry's "View
    // GTPL Saathi (nav access)") delegates it to staff too. Also respects the "GTPL
    // Saathi" Settings tab's own showInNav toggle — see GtplSaathiSettingsTab.js.
    icon: Ticket, label: 'GTPL', key: 'gtpl', adminOnly: true, moduleKey: 'gtplSaathi', groupTo: '/gtpl',
    children: [
      // Start page: every GTPL screen grouped by task, with login / retry-queue status.
      { to: '/gtpl', icon: LayoutGrid, label: 'GTPL Home' },
      { to: '/gtpl-saathi-subscriber', icon: Search, label: 'Subscriber Lookup' },
      { to: '/gtpl-saathi-multi-renew', icon: ListChecks, label: 'Multi Renew' },
      { to: '/gtpl-saathi-modify', icon: Pencil, label: 'Modify' },
      { to: '/gtpl-saathi-hardware-replacement', icon: Repeat, label: 'Hardware Replacement' },
      // Bulk-add one A la Carte channel (e.g. free AASTHA) to every active customer.
      { to: '/gtpl-alacarte-bulk', icon: PlusSquare, label: 'A la Carte Bulk' },
      { to: '/gtpl-bulk-change-order', icon: Replace, label: 'Bulk Change Order' },
      // Compares active CATV customers' CRM data (mobile, entity, STB) against the live GTPL
      // Saathi record — own moduleKey action 'crmCheck', separate from 'modify' (which the
      // page's "Update GTPL's Data" mobile fix still requires server-side).
      { to: '/gtpl-vs-crm', icon: GitCompareArrows, label: 'GTPL vs CRM' },
      // CRM vs GTPL expiry date only, for the customer types ticked (DTD by default). Own
      // moduleKey action 'expiryCheck'; check-only, writes nothing.
      { to: '/gtpl-expiry-check', icon: CalendarCheck, label: 'Expiry Check' },
      // Spare (balance) boxes GTPL shows for each entity — read from Saathi's entity-wise STB
      // report. Own moduleKey action 'boxStock'.
      { to: '/gtpl-box-stock', icon: Package, label: 'Box Stock' },
      // Opposite direction from GTPL vs CRM above — accounts active on GTPL Saathi that have
      // no matching Account No. in the CRM at all. Own moduleKey action 'orphanCheck'.
      { to: '/gtpl-not-in-crm', icon: UserX, label: 'GTPL Not In CRM' },
      // Narrower cousin of GTPL vs CRM above — for accounts that DO match a CRM customer,
      // flags a box model/serial mismatch, read straight off the same bulk report the "Not
      // In CRM" check above already scrapes (no extra per-customer GTPL round-trip). Own
      // moduleKey action 'stbCheck'.
      { to: '/gtpl-stb-mismatch', icon: Tv, label: 'GTPL STB Mismatch' },
      // Every inactive GTPL account with its box (serial / model) and entity, filterable, plus a
      // per-account STB-issue pending/done flag staff maintain. Scraped from GTPL's "INACTIVE
      // SUBSCRIBER LIST REPORT". Own moduleKey actions 'inactiveAccounts' / 'stbIssueUpdate'.
      { to: '/gtpl-90-days', icon: CalendarClock, label: '90 Days (Account)' },
      { to: '/gtpl-saathi-ticket', icon: Ticket, label: 'eSupport Tickets' },
      // Its own page, not folded into Session & Settings below — this list can grow large
      // over time (every write action queued by a session expiry lands here).
      { to: '/gtpl-saathi-retry-queue', icon: History, label: 'Retry Queue' },
      // Full audit trail of every real write action (success AND failure) — the Retry
      // Queue above only ever gets a row when the session cookie had expired.
      { to: '/gtpl-saathi-logs', icon: ScrollText, label: 'Logs' },
      // Own route (not /settings, which is hard admin-only with no moduleKey bypass — see
      // App.js's ProtectedRoute) so this is staff-delegable like the rest of the group.
      // Reading session status only needs the base 'view' right; pasting a fresh cookie or
      // changing settings still requires 'manageSession' (enforced server-side).
      { to: '/gtpl-saathi-settings', icon: KeyRound, label: 'Session & Settings' },
    ],
  },
  { section: 'ADMIN' },
  { to: '/money', icon: Wallet, label: 'Money Manager', moduleKey: 'money' },
  {
    // Per-child gating like Complaints/Requests: Message Center stays delegable to staff,
    // the two API pages are admin-or-module-right, same as when they were standalone items.
    icon: MessageCircle, label: 'WhatsApp API', key: 'whatsappApi', groupTo: '/messaging',
    children: [
      { to: '/kapso', icon: BadgeCheck, label: 'Official API (Kapso)', moduleKey: 'kapso' },
      { to: '/whatsapp-api', icon: MessageCircle, label: 'Un-official API (Self Hosted)', moduleKey: 'whatsappGateway' },
      { to: '/messaging', icon: Send, label: 'Message Center', moduleKey: 'messaging' },
    ],
  },
  {
    // Agent Collection, Staff Users and Site Roles grouped under one "Users" entry
    // (2026-09-05). No parent-level adminOnly/moduleKey — deliberately mirrors the
    // Complaints/Requests group's per-child gating rather than Customer Updates' gate-at-
    // the-parent-only one, since Agent Collection must stay delegable to non-admin staff
    // via its own moduleKey exactly as it was as a standalone item; Staff Users and Site
    // Roles keep their own child-level adminOnly so they stay hard-admin-only.
    icon: UserCog, label: 'Users', key: 'users', groupTo: '/users',
    children: [
      { to: '/agent-collection', icon: UserCheck, label: 'Agent Collection', moduleKey: 'agentCollection' },
      { to: '/users', icon: UserCog, label: 'Staff Users', adminOnly: true },
      { to: '/site-roles', icon: ShieldCheck, label: 'Site Roles', adminOnly: true },
    ],
  },
  { to: '/change-ledger', icon: History, label: 'System Logs', adminOnly: true, moduleKey: 'changeLedger' },
  { to: '/recycle-bin', icon: Trash2, label: 'Recycle Bin', adminOnly: true, moduleKey: 'recycleBin' },
  {
    // Groups every bulk/one-off customer-data maintenance tool (historical payment loads,
    // outstanding-balance corrections, expiry re-syncs, and the CATV<->Broadband account
    // linker) under one admin-only subsection instead of five separate top-level entries.
    // These previously each carried their own Site Role moduleKey (historyImport, syncExpiry,
    // linkCustomers) for fine-grained non-admin access; the dropdown pattern here (same as
    // Customers/Payment History Import above) only gates at the parent, so a non-admin
    // granted one of those individual rights no longer sees just that one entry — full admin
    // is required for the whole group now. Revisit if a staff/agent role ever needs one of
    // these specifically (Site Roles had 0 staff users assigned as of 2026-07-17).
    icon: FileClock, label: 'Customer Updates', key: 'customerUpdates', adminOnly: true, groupTo: '/history-import',
    children: [
      { to: '/history-import', icon: Tv, label: 'Payment History – CableTV' },
      { to: '/history-import-broadband', icon: Wifi, label: 'Payment History – Broadband' },
      { to: '/balance-update', icon: FileSpreadsheet, label: 'Balance Update' },
      { to: '/needs-plan', icon: Wifi, label: 'Assign Broadband Plans' },
      { to: '/sync-import-catv', icon: Tv, label: 'Sync Expiry – CableTV' },
      { to: '/sync-import', icon: Wifi, label: 'Sync Expiry – Broadband' },
      { to: '/sync-import-catv-hardware', icon: Tv, label: 'Sync CATV Hardware' },
      { to: '/link-customers', icon: Link, label: 'Link CableTV ↔ Broadband' },
      { to: '/export-contacts', icon: Phone, label: 'Export Contacts' },
    ],
  },
  // Laxmi's own admin tool, not part of the product: hidden on the demo and every operator hostname.
  ...(IS_LAXMI_HOST ? [{ to: '/claude-chat', icon: Bot, label: 'Claude Code', adminOnly: true }] : []),
  { to: '/gst-billing', icon: FileSpreadsheet, label: 'GST Billing', adminOnly: true },
  { to: '/settings', icon: Settings, label: 'Settings', adminOnly: true },
];

// Matches a dropdown child's `to` against the current location, handling both
// query-string variants (Customers: /customers?customerType=...) and plain routes (Settings).
export function childMatches(child, pathname, search) {
  const qIdx = child.to.indexOf('?');
  if (qIdx === -1) return pathname === child.to || pathname.startsWith(child.to + '/');
  return pathname === child.to.slice(0, qIdx) && search === child.to.slice(qIdx);
}

// Flat, pickable list derived from navItems for the App View admin editor (bottom-tab
// picker + More-menu reordering): section headers stripped. Dropdown groups contribute BOTH
// a combined picker entry (`isGroupPicker: true` — the app shell should ask which child to
// open rather than guessing) AND each child as its own separately pickable entry, so an
// admin can put "CableTV" or "Service Requests" directly on the bottom bar/More menu instead
// of always going through the combined entry.
export const flatNavItems = navItems
  .filter(item => !item.section)
  .flatMap(item => (item.children
    ? [
        // `to` is only the fallback destination if something navigates to the group entry
        // directly instead of opening the picker — it used to be hardcoded to /customers,
        // which sent every non-Customers group to the customer list.
        { id: item.key, to: item.groupTo || item.children[0].to, icon: item.icon, label: item.label, adminOnly: item.adminOnly, moduleKey: item.moduleKey, isGroupPicker: true, children: item.children },
        // A child's own moduleKey/adminOnly wins over the group's, so a child-level gate
        // (Complaints/Requests, GTPL's Session & Settings) isn't flattened away into the parent's.
        // `groupKey` lets a directly-picked child (App View bottom tab/More menu) still
        // respect a group-level toggle like GTPL's own showInNav preference.
        ...item.children.map(child => ({ id: child.to, to: child.to, icon: child.icon, label: child.label, adminOnly: child.adminOnly || item.adminOnly, moduleKey: child.moduleKey || item.moduleKey, groupKey: item.key })),
      ]
    : [{ id: item.to, to: item.to, icon: item.icon, label: item.label, adminOnly: item.adminOnly, moduleKey: item.moduleKey }]
  ));

export function getPageTitle(pathname) {
  if (pathname.startsWith('/customers')) return 'Customers';
  const matches = (to) => pathname === to || pathname.startsWith(to + '/');
  for (const n of navItems) {
    if (n.to && matches(n.to)) return n.label;
    if (n.children) {
      const child = n.children.find(c => matches(c.to.split('?')[0]));
      if (child) return child.label;
    }
  }
  // Network Overview's sub-pages (/technical/onus, /alarms…) open inside this same shell.
  if (pathname.startsWith('/technical')) return techTitle(pathname);
  return 'CRM';
}
